import React, { useState, useMemo, useEffect } from "react";
import Papa from "papaparse";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import {
  Upload,
  Filter,
  Search,
  Tag,
  ExternalLink,
  MessageSquare,
  Briefcase,
  AlertCircle,
  LayoutDashboard,
  List,
  Activity,
  Sparkles,
  Check,
  Copy,
  BrainCircuit,
  Target,
  Users,
  Zap,
  ArrowUpRight,
} from "lucide-react";
import { format, parseISO, isValid, compareDesc } from "date-fns";
import _ from "lodash";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// --- Utility Functions ---

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// --- LocalStorage Helpers ---
const LS_OPENAI_KEY = "AGENTIC_OPENAI_KEY";
const LS_PLAYBOOK = "AGENTIC_PLAYBOOK_V2";

function getLocal(k) {
  try {
    return localStorage.getItem(k) || "";
  } catch {
    return "";
  }
}
function setLocal(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch {}
}

// --- UI Components ---

const Card = ({ children, className = "" }) => (
  <div
    className={cn(
      "bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-xl shadow-2xl",
      className
    )}
  >
    {children}
  </div>
);

const Button = ({
  children,
  variant = "primary",
  onClick,
  className,
  disabled,
}) => {
  const styles = {
    primary:
      "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)] border-blue-500",
    secondary:
      "bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700",
    ghost:
      "bg-transparent hover:bg-slate-800 text-slate-400 hover:text-white border-transparent",
    magic:
      "bg-gradient-to-r from-purple-600 to-blue-600 text-white border-none hover:opacity-90",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "px-4 py-2 rounded-lg font-medium text-sm border transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed",
        styles[variant],
        className
      )}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, type = "default", className }) => {
  const styles = {
    default: "bg-slate-800 text-slate-300 border-slate-700",
    brand: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    score: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono",
    pain: "bg-red-500/10 text-red-400 border-red-500/20",
    displacement: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    exec: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  };
  return (
    <span
      className={cn(
        "px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-semibold border inline-flex items-center gap-1",
        styles[type] || styles.default,
        className
      )}
    >
      {children}
    </span>
  );
};

// --- Scoring Algorithm ---
const calculateImpactScore = (item) => {
  let score = 0;
  const text = (item.Title + " " + item.Snippet).toLowerCase();

  // 1. Intent Signals (+40)
  if (
    text.includes("looking for") ||
    text.includes("recommend") ||
    text.includes("pricing") ||
    text.includes("cost")
  )
    score += 40;

  // 2. Displacement/Competitor signals (+30)
  if (
    text.includes("vs") ||
    text.includes("alternative") ||
    text.includes("switch") ||
    text.includes("expensive")
  )
    score += 30;

  // 3. Pain Signals (+20)
  if (
    text.includes("pain") ||
    text.includes("problem") ||
    text.includes("issue") ||
    text.includes("fail") ||
    text.includes("slow")
  )
    score += 20;

  // 4. Role/Authority (+15)
  if (
    text.includes("vp") ||
    text.includes("head of") ||
    text.includes("director") ||
    text.includes("cmo") ||
    text.includes("cro")
  )
    score += 15;

  // 5. Platform Weight (+10)
  if (item.Platform === "LinkedIn" || item.Platform === "G2") score += 10;

  return Math.min(score, 100); // Cap at 100
};

// --- Main Application ---

export default function App() {
  const [tab, setTab] = useState("radar"); // 'radar' | 'list' | 'playbook'
  const [data, setData] = useState([]);
  const [playbook, setPlaybook] = useState(() => {
    try {
      return JSON.parse(getLocal(LS_PLAYBOOK)) || [];
    } catch {
      return [];
    }
  });

  // Smart Grouping Toggle
  const [smartGroup, setSmartGroup] = useState("All"); // 'All', 'Displacement', 'Executive', 'Greenfield'

  // Load Data
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const processed = results.data.map((row, index) => {
          const brandList = row.Brand
            ? row.Brand.split(" | ").filter(
                (x) => x !== "Unspecified / multi-brand"
              )
            : [];

          const item = {
            ...row,
            id: index,
            brandList,
            pillarList: row.Pillars ? row.Pillars.split(" | ") : [],
            cleanDate:
              row.PostDate && isValid(parseISO(row.PostDate))
                ? row.PostDate
                : null,
          };

          // Calculate Score immediately
          item.impactScore = calculateImpactScore(item);

          // Tag Categories
          const text = (item.Title + " " + item.Snippet).toLowerCase();
          item.isDisplacement =
            text.includes("vs") ||
            text.includes("alternative") ||
            text.includes("replace");
          item.isExecutive =
            text.includes("vp") ||
            text.includes("head of") ||
            text.includes("director");
          item.isGreenfield =
            (text.includes("pain") || text.includes("manual")) &&
            brandList.length === 0;

          return item;
        });

        // Sort by Score DESC by default
        setData(processed.sort((a, b) => b.impactScore - a.impactScore));
      },
    });
  };

  // --- Filtering & Grouping Logic ---
  const filteredData = useMemo(() => {
    let result = data;
    if (smartGroup === "Displacement")
      result = result.filter((d) => d.isDisplacement);
    if (smartGroup === "Executive")
      result = result.filter((d) => d.isExecutive);
    if (smartGroup === "Greenfield")
      result = result.filter((d) => d.isGreenfield);
    return result;
  }, [data, smartGroup]);

  // --- Playbook Actions ---
  const togglePlaybook = (item) => {
    if (playbook.find((p) => p.id === item.id)) {
      setPlaybook(playbook.filter((p) => p.id !== item.id));
    } else {
      setPlaybook([...playbook, item]);
    }
  };
  useEffect(() => setLocal(LS_PLAYBOOK, JSON.stringify(playbook)), [playbook]);

  // --- Sub Components ---
  const ScoreRing = ({ score }) => {
    const color =
      score > 80
        ? "text-emerald-400 border-emerald-500"
        : score > 50
        ? "text-blue-400 border-blue-500"
        : "text-slate-500 border-slate-700";
    return (
      <div
        className={cn(
          "w-10 h-10 rounded-full border-2 flex items-center justify-center font-bold text-xs bg-slate-900",
          color
        )}
      >
        {score}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500/30 pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg shadow-lg shadow-blue-500/20">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">
              Agentic<span className="text-slate-500">Radar</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 border border-slate-700 hover:border-slate-500 text-slate-300 rounded-md cursor-pointer text-xs font-medium transition-all">
            <Upload className="w-3 h-3" /> <span>Source Data</span>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>

          <div className="h-6 w-px bg-slate-800 mx-2" />

          <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-800">
            <button
              onClick={() => setTab("radar")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2",
                tab === "radar"
                  ? "bg-slate-800 text-white shadow"
                  : "text-slate-500 hover:text-slate-300"
              )}
            >
              <Activity className="w-3 h-3" /> Radar
            </button>
            <button
              onClick={() => setTab("list")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2",
                tab === "list"
                  ? "bg-slate-800 text-white shadow"
                  : "text-slate-500 hover:text-slate-300"
              )}
            >
              <List className="w-3 h-3" /> Raw Data
            </button>
            <button
              onClick={() => setTab("playbook")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-2",
                tab === "playbook"
                  ? "bg-slate-800 text-white shadow"
                  : "text-slate-500 hover:text-slate-300"
              )}
            >
              <BrainCircuit className="w-3 h-3" /> Playbook{" "}
              <span className="bg-blue-600 text-[10px] px-1.5 rounded-full">
                {playbook.length}
              </span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6">
        {data.length === 0 && (
          <div className="flex flex-col items-center justify-center h-[60vh] border border-dashed border-slate-800 rounded-2xl bg-slate-900/30">
            <Activity className="w-12 h-12 text-slate-700 mb-4" />
            <p className="text-slate-500">Upload CSV to activate Radar</p>
          </div>
        )}

        {data.length > 0 && tab === "radar" && (
          <div className="animate-in fade-in slide-in-from-bottom-4 space-y-6">
            {/* 1. SMART LENSES (The "Creative Grouping") */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button
                onClick={() => setSmartGroup("All")}
                className={cn(
                  "p-4 rounded-xl border text-left transition-all hover:scale-[1.02]",
                  smartGroup === "All"
                    ? "bg-slate-800 border-slate-600 shadow-xl"
                    : "bg-slate-900/50 border-slate-800 opacity-70"
                )}
              >
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">
                  Total Signals
                </div>
                <div className="text-3xl font-bold text-white">
                  {data.length}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Unfiltered View
                </div>
              </button>

              <button
                onClick={() => setSmartGroup("Displacement")}
                className={cn(
                  "p-4 rounded-xl border text-left transition-all hover:scale-[1.02] group",
                  smartGroup === "Displacement"
                    ? "bg-orange-950/30 border-orange-500/50 shadow-lg shadow-orange-900/20"
                    : "bg-slate-900/50 border-slate-800"
                )}
              >
                <div className="flex justify-between items-start">
                  <div className="text-xs text-orange-400 uppercase tracking-wider mb-1 font-bold">
                    ⚔️ Displacement
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="text-3xl font-bold text-white">
                  {data.filter((d) => d.isDisplacement).length}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Mentions "vs", "switch", "alternative"
                </div>
              </button>

              <button
                onClick={() => setSmartGroup("Executive")}
                className={cn(
                  "p-4 rounded-xl border text-left transition-all hover:scale-[1.02] group",
                  smartGroup === "Executive"
                    ? "bg-purple-950/30 border-purple-500/50 shadow-lg shadow-purple-900/20"
                    : "bg-slate-900/50 border-slate-800"
                )}
              >
                <div className="flex justify-between items-start">
                  <div className="text-xs text-purple-400 uppercase tracking-wider mb-1 font-bold">
                    👔 Executive Radar
                  </div>
                  <Users className="w-4 h-4 text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="text-3xl font-bold text-white">
                  {data.filter((d) => d.isExecutive).length}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Matches "VP", "Head of", "Director"
                </div>
              </button>

              <button
                onClick={() => setSmartGroup("Greenfield")}
                className={cn(
                  "p-4 rounded-xl border text-left transition-all hover:scale-[1.02] group",
                  smartGroup === "Greenfield"
                    ? "bg-emerald-950/30 border-emerald-500/50 shadow-lg shadow-emerald-900/20"
                    : "bg-slate-900/50 border-slate-800"
                )}
              >
                <div className="flex justify-between items-start">
                  <div className="text-xs text-emerald-400 uppercase tracking-wider mb-1 font-bold">
                    📉 Greenfield Pain
                  </div>
                  <Zap className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="text-3xl font-bold text-white">
                  {data.filter((d) => d.isGreenfield).length}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  High pain, no competitor named
                </div>
              </button>
            </div>

            {/* 2. PRIORITY FEED */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Feed */}
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    {smartGroup === "All"
                      ? "Prioritized Feed"
                      : `${smartGroup} Opportunities`}
                    <span className="text-xs font-normal text-slate-500 bg-slate-900 px-2 py-0.5 rounded-full border border-slate-800">
                      Sorted by Impact Score
                    </span>
                  </h3>
                </div>

                {filteredData.slice(0, 100).map((item) => (
                  <div
                    key={item.id}
                    className="group relative bg-slate-900/50 border border-slate-800 hover:border-slate-600 rounded-xl p-5 transition-all hover:shadow-xl"
                  >
                    <div className="absolute top-5 right-5 flex flex-col items-end gap-2">
                      <ScoreRing score={item.impactScore} />
                      <button
                        onClick={() => togglePlaybook(item)}
                        className={cn(
                          "p-2 rounded-full transition-colors",
                          playbook.find((p) => p.id === item.id)
                            ? "bg-blue-600 text-white"
                            : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                        )}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-500 font-mono">
                        {item.cleanDate || "Unknown Date"}
                      </span>
                      <span className="text-xs text-slate-600">•</span>
                      <Badge type="default" className="text-[10px]">
                        {item.Platform}
                      </Badge>
                    </div>

                    <h4 className="text-lg font-semibold text-slate-200 pr-16 mb-2 group-hover:text-blue-400 transition-colors">
                      <a
                        href={item.PostURL}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {item.Title}
                      </a>
                    </h4>

                    <p className="text-sm text-slate-400 mb-4 pr-12 line-clamp-3 leading-relaxed">
                      "{item.Snippet}"
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {item.brandList.map((b) => (
                        <Badge key={b} type="brand">
                          {b}
                        </Badge>
                      ))}
                      {item.isDisplacement && (
                        <Badge type="displacement">Displacement Risk</Badge>
                      )}
                      {item.isExecutive && (
                        <Badge type="exec">Decision Maker</Badge>
                      )}
                      {item.isGreenfield && (
                        <Badge type="pain">Greenfield Opp</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: Quick Analytics for this Group */}
              <div className="space-y-6">
                <Card className="p-5 sticky top-24">
                  <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">
                    Top Vendors in View
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(
                      _.countBy(filteredData.flatMap((d) => d.brandList))
                    )
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10)
                      .map(([brand, count], i) => (
                        <div
                          key={brand}
                          className="flex items-center justify-between text-sm"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-slate-600 font-mono text-xs">
                              0{i + 1}
                            </span>
                            <span className="text-slate-300">{brand}</span>
                          </div>
                          <span className="text-slate-500">{count}</span>
                        </div>
                      ))}
                  </div>

                  <div className="h-px bg-slate-800 my-6" />

                  <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">
                    Grouping Logic
                  </h3>
                  <div className="text-xs text-slate-500 space-y-2">
                    <p>
                      <strong>Impact Score (0-100):</strong> Calculated based on
                      buying intent keywords, known competitor names, and
                      executive job titles found in the snippet.
                    </p>
                    <p>
                      <strong>Displacement:</strong> Checks for "vs",
                      "alternative", "expensive".
                    </p>
                    <p>
                      <strong>Greenfield:</strong> Checks for "pain" without
                      specific brand mentions.
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* RAW DATA TAB (Existing logic preserved) */}
        {tab === "list" && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden animate-in fade-in">
            <table className="w-full text-left text-sm text-slate-400">
              <thead className="bg-slate-950 text-xs uppercase font-bold text-slate-500">
                <tr>
                  <th className="p-4">Score</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Title / Snippet</th>
                  <th className="p-4">Tags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {data.slice(0, 200).map((row) => (
                  <tr key={row.id} className="hover:bg-slate-800/50">
                    <td className="p-4 font-mono text-white">
                      {row.impactScore}
                    </td>
                    <td className="p-4 whitespace-nowrap">{row.cleanDate}</td>
                    <td className="p-4 max-w-xl">
                      <div className="text-white font-medium truncate">
                        {row.Title}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {row.Snippet}
                      </div>
                    </td>
                    <td className="p-4">
                      {row.isDisplacement && (
                        <span className="text-[10px] bg-orange-900 text-orange-400 px-1 rounded mr-1">
                          VS
                        </span>
                      )}
                      {row.isExecutive && (
                        <span className="text-[10px] bg-purple-900 text-purple-400 px-1 rounded">
                          EXEC
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PLAYBOOK TAB (Simplified for this view) */}
        {tab === "playbook" && (
          <div className="max-w-3xl mx-auto space-y-4 animate-in fade-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                Strategic Playbook
              </h2>
              <Button
                variant="secondary"
                onClick={() => {
                  const text = playbook
                    .map((p) => `${p.Title}\n${p.Snippet}\n${p.PostURL}\n`)
                    .join("\n---\n");
                  navigator.clipboard.writeText(text);
                  alert("Copied to clipboard!");
                }}
              >
                Copy All for AI
              </Button>
            </div>
            {playbook.length === 0 ? (
              <p className="text-slate-500 text-center py-10">
                No signals flagged yet.
              </p>
            ) : (
              playbook.map((item) => (
                <Card key={item.id} className="p-4 flex gap-4">
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-1">{item.Title}</h4>
                    <p className="text-sm text-slate-400 mb-2">
                      {item.Snippet}
                    </p>
                    <div className="text-xs text-slate-600 font-mono">
                      {item.PostURL}
                    </div>
                  </div>
                  <button
                    onClick={() => togglePlaybook(item)}
                    className="text-slate-600 hover:text-red-500 self-start"
                  >
                    ×
                  </button>
                </Card>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
